import {
  getTeethsFromLowerArch,
  getTeethsFromUpperArch,
  sortTeethAsc,
} from "../item.utils";
import { LowerArchTeeth, UpperArchTeeth } from "../../types/constants";
import { Order, Product } from "../../types/interfaces";
import { PartialDentureOptions } from "../workflows.utils";

import { ItemMaterials, ItemType } from "../../types/enums";

export type HooksAndOptionsMappedValues = Record<string, unknown> & {
  hookChoice: string;
  hooksPosition?: {
    metal?: number[];
    valplast?: number[];
    acetal?: number[];
  };
  hooksMaterial: ItemMaterials;
  options?: string[];
};

type mappedTeeth = {
  hookMaterial: ItemMaterials | "no-hook";
  teeth: number[];
};

const createHooks = (hooks: mappedTeeth[]) => {
  return hooks
    .map(hook => {
      if (hook.hookMaterial !== "no-hook") {
        return {
          hooksPosition: {
            set: sortTeethAsc(hook.teeth),
          },
          hooksMaterial: {
            connect: {
              id: hook.hookMaterial,
            },
          },
        };
      }
      return undefined;
    })
    .filter(r => r !== undefined);
};

const mapHooksAndOptions = (
  hooks: mappedTeeth[],
  options: Set<string>,
  id: string,
  partialDentureId: string,
  isMaxillary: boolean
) => {
  return {
    data: {
      partialDenture: {
        update: {
          data: {
            hooks: {
              deleteMany: {
                partialDentureId: {
                  equals: partialDentureId,
                },
              },
              create: createHooks(hooks) ?? undefined,
            },
            palatalMetalBand: {
              set: isMaxillary
                ? options.has(PartialDentureOptions.PALATAL_METAL_BAND)
                : false,
            },
            metalMesh: {
              set: options.has(PartialDentureOptions.METAL_MESH),
            },
            customTray: {
              set: false, // This field is deprecated and must be decommissioned. Custom Tray are there own product now.
            },
            lingualMetalBand: {
              set: !isMaxillary
                ? options.has(PartialDentureOptions.LINGUAL_METAL_BAND)
                : false,
            },
          },
          where: {
            id: {
              equals: partialDentureId,
            },
          },
        },
      },
    },
    where: {
      id: id,
    },
  };
};

const getHookByArc = ({
  mappedValues,
  product,
}: {
  mappedValues: HooksAndOptionsMappedValues;
  product: Product;
}): { maxillaryTeeth: mappedTeeth[]; mandibularTeeth: mappedTeeth[] } => {
  const upperTeeth = [];
  const lowerTeeth = [];
  if (
    [
      ...(mappedValues.hooksPosition?.metal ?? []),
      ...(mappedValues.hooksPosition?.valplast ?? []),
      ...(mappedValues.hooksPosition?.acetal ?? []),
    ].length === 0 &&
    mappedValues.hooksMaterial !== null
  ) {
    upperTeeth.push({
      hookMaterial: mappedValues.hooksMaterial,
      teeth: [],
    });
    lowerTeeth.push({
      hookMaterial: mappedValues.hooksMaterial,
      teeth: [],
    });
  }
  if (
    mappedValues.hooksPosition?.metal?.length > 0 &&
    getTeethsFromUpperArch(mappedValues.hooksPosition.metal ?? []).length > 0
  ) {
    upperTeeth.push({
      hookMaterial: ItemMaterials.METAL_NON_PRECIOUS,
      teeth: getTeethsFromUpperArch(mappedValues.hooksPosition.metal ?? []),
    });
  }
  if (
    mappedValues.hooksPosition?.valplast?.length > 0 &&
    getTeethsFromUpperArch(mappedValues.hooksPosition.valplast ?? []).length > 0
  ) {
    upperTeeth.push({
      hookMaterial: ItemMaterials.VALPLAST,
      teeth: getTeethsFromUpperArch(mappedValues.hooksPosition?.valplast ?? []),
    });
  }
  if (
    mappedValues.hooksPosition?.acetal?.length > 0 &&
    getTeethsFromUpperArch(mappedValues.hooksPosition.acetal ?? []).length > 0
  ) {
    upperTeeth.push({
      hookMaterial: ItemMaterials.ACETAL,
      teeth: getTeethsFromUpperArch(mappedValues.hooksPosition.acetal ?? []),
    });
  }

  if (
    mappedValues.hooksPosition?.metal?.length > 0 &&
    getTeethsFromLowerArch(mappedValues.hooksPosition.metal ?? []).length > 0
  ) {
    lowerTeeth.push({
      hookMaterial: ItemMaterials.METAL_NON_PRECIOUS,
      teeth: getTeethsFromLowerArch(mappedValues.hooksPosition.metal ?? []),
    });
  }
  if (
    mappedValues.hooksPosition?.valplast?.length > 0 &&
    getTeethsFromLowerArch(mappedValues.hooksPosition.valplast ?? []).length > 0
  ) {
    lowerTeeth.push({
      hookMaterial: ItemMaterials.VALPLAST,
      teeth: getTeethsFromLowerArch(mappedValues.hooksPosition?.valplast ?? []),
    });
  }
  if (
    mappedValues.hooksPosition?.acetal?.length > 0 &&
    getTeethsFromLowerArch(mappedValues.hooksPosition.acetal ?? []).length > 0
  ) {
    lowerTeeth.push({
      hookMaterial: ItemMaterials.ACETAL,
      teeth: getTeethsFromLowerArch(mappedValues.hooksPosition.acetal ?? []),
    });
  }
  return {
    mandibularTeeth: lowerTeeth as mappedTeeth[],
    maxillaryTeeth: upperTeeth as mappedTeeth[],
  };
};

const saveHooksAndOptionsHandler = (
  mappedValues: HooksAndOptionsMappedValues,
  product: Product
) => {
  const options = new Set(mappedValues.options || []);
  const { mandibularTeeth, maxillaryTeeth } = getHookByArc({
    mappedValues: mappedValues,
    product: product,
  });
  const returnValue = [];
  const getMandibularItem = product.removableItem.find(
    item => getTeethsFromLowerArch(item.teeth).length > 0
  );
  const getMaxillaryItem = product.removableItem.find(
    item => getTeethsFromUpperArch(item.teeth).length > 0
  );
  if (getMandibularItem) {
    returnValue.push(
      mapHooksAndOptions(
        mandibularTeeth as mappedTeeth[],
        options,
        getMandibularItem.id,
        getMandibularItem.partialDenture.id,
        false
      )
    );
  }
  if (getMaxillaryItem) {
    returnValue.push(
      mapHooksAndOptions(
        maxillaryTeeth,
        options,
        getMaxillaryItem.id,
        getMaxillaryItem.partialDenture.id,
        true
      )
    );
  }
  return returnValue;
};

const getCustomTrayProductsToCreate = (
  product: Product,
  mappedValues: HooksAndOptionsMappedValues
) => {
  if (!mappedValues.options?.includes(PartialDentureOptions.CUSTOM_TRAY))
    return [];

  const teethToManufacture = product.removableItem
    .filter(i => i.itemType === ItemType.PARTIAL_DENTURE)
    .flatMap(i => i.teethToManufacture);

  const customTrayOnLowerArch =
    getTeethsFromLowerArch(teethToManufacture).length > 0;
  const customTrayOnUpperArch =
    getTeethsFromUpperArch(teethToManufacture).length > 0;

  // This case should not happen as we check for CUSTOM_TRAY option above
  if (!customTrayOnLowerArch && !customTrayOnUpperArch) return [];

  const lowerTeeth = customTrayOnLowerArch ? LowerArchTeeth : [];
  const upperTeeth = customTrayOnUpperArch ? UpperArchTeeth : [];

  return [
    {
      productType: ItemType.CUSTOM_TRAY,
      teeth: [...lowerTeeth, ...upperTeeth],
      removableItem: {
        create: [
          ...(lowerTeeth.length
            ? [
                {
                  itemType: ItemType.CUSTOM_TRAY,
                  teeth: lowerTeeth,
                },
              ]
            : []),
          ...(upperTeeth.length
            ? [
                {
                  itemType: ItemType.CUSTOM_TRAY,
                  teeth: upperTeeth,
                },
              ]
            : []),
        ],
      },
    },
  ];
};

export const updateHooksAndOptions = async (
  updateOrder: (data: {
    variables: {
      data: any;
      where: { id: string };
    };
  }) => Promise<Order>,
  mappedValues: HooksAndOptionsMappedValues,
  orderId: string,
  product: Product
) => {
  try {
    if (!product.removableItem?.[0]?.partialDenture) {
      throw new Error("No partial denture found in product");
    }

    const customTrayProductsToCreate = getCustomTrayProductsToCreate(
      product,
      mappedValues
    );

    return await updateOrder({
      variables: {
        data: {
          products: {
            update: {
              data: {
                removableItem: {
                  update: saveHooksAndOptionsHandler(mappedValues, product),
                },
              },
              where: {
                id: product.id,
              },
            },
            ...(customTrayProductsToCreate.length
              ? { create: customTrayProductsToCreate }
              : {}),
          },
        },
        where: {
          id: orderId,
        },
      },
    });
  } catch (error) {
    console.error("Failed to update hooks and options:", error);
    throw error;
  }
};
